<template>
  <a-breadcrumb
    v-bind="attrs"
    ref="breadcrumbRef"
    :class="[
      bem.b(),
      bem.is(`${props.size}`),
      props.type === 'card' ? bem.is('card') : bem.is('default'),
    ]"
  >
    <template v-if="props.items && props.items.length > 0">
      <a-breadcrumb-item
        v-for="(item, index) in processedItems"
        :key="item.key || index"
        :class="[
          bem.e('item'),
          item.type === 'ellipsis' ? bem.is('ellipsis') : '',
        ]"
        :style="{
          'margin-right':
            props.type === 'card' ? `${props.separatorSpace}px` : '',
        }"
        :href="
          item.type === 'ellipsis' || !props.isLinkSkip ? undefined : item.url
        "
        @click="(e: MouseEvent) => handleItemClick(e, item, index)"
      >
        <template v-if="item.type === 'ellipsis'">
          <div :class="bem.e('ellipsis')">
            <ll-tooltip
              v-if="props.showHiddenItem && hiddenItems.length > 0"
              :tip="hiddenItems.map((item) => item.title).join(` / `)"
            >
              <ll-icon icon-name="icon-more" />
            </ll-tooltip>
            <ll-icon v-else icon-name="icon-more" />
          </div>
        </template>

        <template v-else>
          <slot
            v-if="item.key && hasSlot(item.key) && item.key !== 'item'"
            :name="item.key"
            :item="item"
            :index="index"
          />

          <slot
            v-else-if="hasSlot('item')"
            name="item"
            :item="item"
            :index="index"
          />

          <template v-else>
            <div
              :class="bem.e('text-content')"
              @mouseenter="setActiveIndex(index)"
              @mouseleave="activeIndex = null"
            >
              <ll-icon
                v-if="showIcon && item.icon"
                :icon-name="item.icon"
                :color="getIconColor(index)"
              />
              <span
                v-if="item.title"
                :style="getTitleStyle(index)"
                :class="[
                  processedItems.length - 1 === index ? 'last-title' : 'title',
                ]"
              >
                {{ item.title }}
              </span>
            </div>
          </template>
        </template>
      </a-breadcrumb-item>
    </template>

    <slot v-else />

    <template #separator>
      <span v-if="!$slots.separator" :style="separatorStyle">
        {{ props.separator }}
      </span>
      <slot v-else name="separator"></slot>
    </template>
  </a-breadcrumb>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { computed, ref, useAttrs, useSlots, watch } from 'vue'
import { BreadcrumbItem, breadcrumbProps } from './breadcrumb'

defineOptions({ name: 'LlBreadcrumb' })

const bem = createNamespace('breadcrumb')
const attrs = useAttrs()
const slots = useSlots()

const props = defineProps(breadcrumbProps)

// 新增：当前活动的面包屑索引
const activeIndex = ref<number | null>(null)
const breadcrumbRef = ref()

// 新增：获取图标颜色
const getIconColor = (index: number) => {
  const isLast = processedItems.value.length - 1 === index
  const isActive = activeIndex.value === index

  // 优先级：最后一项 > 活动项 > 默认
  if (isLast) return props.lastIconColor
  if (isActive) return props.hoverTitleColor
  return props.iconColor
}

// 新增：获取标题样式
const getTitleStyle = (index: number) => {
  const isLast = processedItems.value.length - 1 === index
  const isActive = activeIndex.value === index

  // 最后一项使用最后一级颜色，活动项使用hover颜色，其它使用默认颜色
  return {
    color: isLast
      ? props.lastTitleColor
      : isActive
        ? props.hoverTitleColor
        : props.titleColor,
  }
}

// 新增：设置当前活动项
const setActiveIndex = (index: number) => {
  // 最后一项不可交互
  if (index < processedItems.value.length - 1) {
    activeIndex.value = index
  }
}

// 存储隐藏的面包屑项
const hiddenItems = ref<BreadcrumbItem[]>([])

// 处理后的面包屑项数组
const processedItems = computed(() => {
  if (!props.items || props.items.length === 0) return []

  // 不需要折叠的情况
  if (props.maxCount <= 0 || props.items.length <= props.maxCount) {
    return props.items
  }

  // 需要折叠的情况
  const result: BreadcrumbItem[] = []

  // 1. 添加第一项
  result.push(props.items[0])

  // 2. 添加省略号项
  result.push({
    type: 'ellipsis',
    key: 'breadcrumb-ellipsis',
    title: '...',
  })

  // 3. 添加最后 maxCount-1 项
  const startIndex = props.items.length - (props.maxCount - 1)
  for (let i = startIndex; i < props.items.length; i++) {
    result.push(props.items[i])
  }

  return result
})

// 分隔符样式
const separatorStyle = computed(() => ({
  'margin-inline': `${props.separatorSpace}px`,
  color: props.separatorColor,
}))

// 检查插槽是否存在
const hasSlot = (name: string) => {
  return !!slots[name]
}

// 处理面包屑项点击事件
const handleItemClick = (
  event: MouseEvent,
  item: BreadcrumbItem,
  index: number,
) => {
  // 优先调用项上的自定义处理器
  if (item.onClick) {
    item.onClick(event, item, index)
  }

  // 处理链接跳转
  if (item.url && props.isLinkSkip) {
    switch (item.target) {
      case '_blank':
        window.open(item.url, '_blank', 'noopener,noreferrer')
        break

      case '_parent':
        if (window.parent !== window) {
          window.parent.location.href = item.url
        } else {
          window.location.href = item.url
        }
        break

      case '_top':
        window.top!.location.href = item.url
        break

      case '_self':
      default:
        window.location.href = item.url
        break
    }
    return
  }
}

// 监听 items 和 maxCount 变化，更新隐藏项
watch(
  () => [props.items, props.maxCount],
  () => {
    hiddenItems.value = []

    if (!props.items || props.items.length === 0) return

    // 不需要折叠的情况
    if (props.maxCount <= 0 || props.items.length <= props.maxCount) return

    // 收集隐藏项 (索引1到startIndex-1)
    const startIndex = props.items.length - (props.maxCount - 1)
    for (let i = 1; i < startIndex; i++) {
      hiddenItems.value.push(props.items[i])
    }
  },
  { immediate: true, deep: true },
)
// // 处理分割线颜色
// watch(
//   () => [props.separatorColor, breadcrumbRef.value],
//   () => {
//     if (props.separatorColor && breadcrumbRef.value) {
//       const antBreadcrumbSeparators =
//         breadcrumbRef.value?.$el?.querySelectorAll('.ant-breadcrumb-separator')
//       if (antBreadcrumbSeparators) {
//         antBreadcrumbSeparators.forEach(
//           (antBreadcrumbSeparator) =>
//             (antBreadcrumbSeparator.style.color = props.separatorColor)
//         )
//       }
//     }
//   },
//   {
//     immediate: true,
//   }
// )
</script>
